import {
  Home,
  User,
  Briefcase,
  ChevronLeft,
  ChevronDown,
  DollarSign,
  CreditCard,
  Newspaper,
  Search,
  HelpCircle,
  LucideIcon,
  ExternalLink,
} from "lucide-react";
import { useState, useRef, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui-store";
import { book } from "@/routers/books";
import { SidebarSearch } from "./sidebar-search";
import IconLogo from "../Icons/IconLogo";
import { hostNames } from "@/constants/hosts";
import { useGetMenuSettings } from "@/hooks/use-menu-settings";
import { cpgJobsUrls } from "@/routers/jobsRedirect";

interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  children?: NavItem[];
  external?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// Top-level direct nav items
const topLevelItems: NavItem[] = [
  {
    label: "Home",
    href: book.userAccount,
    icon: Home,
  },
];

// Base navigation structure (will be augmented with dynamic data)
const getBaseNavigation = (
  subscriptionItem?: { showSubscribeLink: boolean; organizationGuid?: string | null },
  userBrands?: Array<{ brandName: string; brandGuid: string; brandSlug: string }>,
  magazineSubscriptionItem?: { guid: string | null },
  hasMarketplaceListings?: boolean,
  noshBrands?: Array<{ brandName: string; brandGuid: string; brandSlug: string }>,
  userBreweries?: Array<{ breweryGuid: string; breweryName: string; brewerySlug: string }>
): NavGroup[] => {
  const navGroups: NavGroup[] = [
    {
      label: "ACCOUNT",
      items: [
        {
          label: "Account Info",
          href: book.userAccount,
          icon: User,
          children: [
            {
              label: "Contact Info",
              href: book.userUpdate,
              icon: User,
            },
            {
              label: "Public Profile",
              href: book.userPublicProfile,
              icon: User,
            },
            {
              label: "Change Password",
              href: book.userChangePassword,
              icon: User,
            },
            {
              label: "Newsletter Opt-in",
              href: book.newsletter,
              icon: User,
            },
            {
              label: "BevNET Magazine",
              href: magazineSubscriptionItem?.guid
                ? book.magazine.renew(magazineSubscriptionItem.guid)
                : book.magazine.subscribe,
              icon: ExternalLink,
              external: true,
            },
          ],
        },
      ],
    },
    {
      label: "ORDERS & BILLING",
      items: [
        {
          label: "Order History",
          href: book.userOrders,
          icon: DollarSign,
        },
        {
          label: "Pay Invoice",
          href: book.invoices.invoice,
          icon: CreditCard,
        },
      ],
    },
  ];

  // Add Insider Subscription section if user has permission
  if (subscriptionItem?.showSubscribeLink && subscriptionItem?.organizationGuid) {
    navGroups.push({
      label: "SUBSCRIPTION",
      items: [
        {
          label: "Insider Subscription",
          href: `https://app.bevnet.com/subscription/${subscriptionItem.organizationGuid}/overview`,
          icon: ExternalLink,
          external: true,
        },
      ],
    });
  }

  // Add Brands section if user has brands
  if (userBrands && userBrands.length > 0) {
    // Sort brands alphabetically by brand name
    const sortedBrands = [...userBrands].sort((a, b) =>
      a.brandName.localeCompare(b.brandName, undefined, { sensitivity: 'base' })
    );

    navGroups.push({
      label: "BRANDS",
      items: sortedBrands.map((brand) => ({
        label: brand.brandName,
        href: book.brands.brandOptions(brand.brandGuid),
        icon: Briefcase,
        children: [
          {
            label: "Overview",
            href: book.brands.brandOptions(brand.brandGuid),
            icon: Briefcase,
          },
          {
            label: "Brand Details",
            href: book.brands.editBrand(brand.brandGuid),
            icon: Briefcase,
          },
          {
            label: "Products",
            href: book.brands.editProducts(brand.brandGuid),
            icon: Briefcase,
          },
        ],
      })),
    });
  }

  // NOSH brand listings the user owns (edited here since D-24881)
  if (noshBrands && noshBrands.length > 0) {
    const sortedNoshBrands = Array.from(new Map(noshBrands.map((b) => [b.brandGuid, b])).values()).sort((a, b) =>
      a.brandName.localeCompare(b.brandName, undefined, { sensitivity: 'base' })
    );

    navGroups.push({
      label: "NOSH BRANDS",
      items: sortedNoshBrands.map((brand) => ({
        label: brand.brandName,
        href: book.noshBrands.hub(brand.brandGuid),
        icon: Briefcase,
        children: [
          {
            label: "Overview",
            href: book.noshBrands.hub(brand.brandGuid),
            icon: Briefcase,
          },
          {
            label: "Brand Details",
            href: book.noshBrands.edit(brand.brandGuid),
            icon: Briefcase,
          },
          {
            label: "Products",
            href: book.noshBrands.products(brand.brandGuid),
            icon: Briefcase,
          },
          {
            label: "Where to Buy",
            href: book.noshBrands.whereToBuy(brand.brandGuid),
            icon: Briefcase,
          },
        ],
      })),
    });
  }

  if (userBreweries?.length) {
    navGroups.push({ label: 'BREWERIES', items: [
      { label: 'My Breweries', href: book.breweries.list, icon: Briefcase },
      ...[...userBreweries].sort((a, b) => a.breweryName.localeCompare(b.breweryName)).map((brewery) => ({ label: brewery.breweryName, href: book.breweries.edit(brewery.breweryGuid, 'general'), icon: Briefcase,
        children: [ { label: 'Brewery information', href: book.breweries.edit(brewery.breweryGuid, 'general'), icon: Briefcase }, { label: 'Beers', href: book.breweries.edit(brewery.breweryGuid, 'beers'), icon: Briefcase }, { label: 'Annual production', href: book.breweries.edit(brewery.breweryGuid, 'production'), icon: Briefcase } ] }))
    ] });
  }
  // Add remaining sections
  navGroups.push({
    label: "JOBS",
    items: [
      {
        label: "Job Listings",
        href: cpgJobsUrls.listings,
        icon: Briefcase,
        external: true,
      },
      {
        label: "Prepaid Job Packages",
        href: cpgJobsUrls.packages,
        icon: Briefcase,
        external: true,
      },
    ],
  });

  if (hasMarketplaceListings) {
    navGroups.push({
      label: "MARKETPLACE",
      items: [
        {
          label: "Marketplace Listings",
          href: book.marketplace.listings,
          icon: Briefcase,
        },
      ],
    });
  }

  navGroups.push({
    label: "PRESS RELEASES",
    items: [
      {
        label: "Press Releases",
        href: book.pressReleases.pressReleases,
        icon: Newspaper,
      },
    ],
  });

  return navGroups;
};

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const location = useLocation();
  const { data: menuData } = useGetMenuSettings();

  // Build navigation with dynamic data
  const navigation = useMemo(() => {
    return getBaseNavigation(menuData?.subscriptionItem, menuData?.userBrands, menuData?.magazineSubscriptionItem, menuData?.hasMarketplaceListings, menuData?.noshBrands, menuData?.userBreweries);
  }, [menuData]);

  // Track expanded items (for nav items with children)
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Track expanded sections (expanded by default for relevant sections)
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "ACCOUNT",
    "ORDERS & BILLING",
    "SUBSCRIPTION",
    "BRANDS",
    "NOSH BRANDS",
    "BREWERIES",
    "JOBS",
    "MARKETPLACE",
    "PRESS RELEASES",
  ]);

  // Track if search should be focused after sidebar expands
  const [shouldFocusSearch, setShouldFocusSearch] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input after sidebar expands
  useEffect(() => {
    if (shouldFocusSearch && !sidebarCollapsed) {
      // Small delay to allow sidebar to expand
      const timeout = setTimeout(() => {
        searchInputRef.current?.focus();
        setShouldFocusSearch(false);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [shouldFocusSearch, sidebarCollapsed]);

  // Handle search icon click in collapsed mode
  const handleSearchClick = () => {
    setShouldFocusSearch(true);
    toggleSidebar();
  };

  // Toggle item expansion
  const toggleExpanded = (href: string) => {
    setExpandedItems((prev) =>
      prev.includes(href) ? prev.filter((h) => h !== href) : [...prev, href]
    );
  };

  // Toggle section expansion
  const toggleSection = (label: string) => {
    setExpandedSections((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  // Check if any child is active
  const isChildActive = (children: NavItem[] | undefined) => {
    if (!children) return false;
    return children.some(
      (child) =>
        location.pathname === child.href ||
        (child.href !== "/" && location.pathname.startsWith(child.href))
    );
  };

  // Check if any item in a section is active
  const isSectionActive = (items: NavItem[]) => {
    return items.some(
      (item) =>
        location.pathname === item.href ||
        (item.href !== "/" && location.pathname.startsWith(item.href)) ||
        isChildActive(item.children)
    );
  };

  // Auto-expand items with active children
  const isExpanded = (item: NavItem) => {
    if (isChildActive(item.children)) return true;
    return expandedItems.includes(item.href);
  };

  // Check if section is expanded
  const isSectionExpanded = (group: NavGroup) => {
    return expandedSections.includes(group.label);
  };

  // Build searchable items list for the search component
  const searchableItems = [
    // Include top-level items
    ...topLevelItems.map((item) => ({
      label: item.label,
      href: item.href,
      groupLabel: undefined as string | undefined,
      parentLabel: undefined as string | undefined,
    })),
    // Include items from navigation groups
    ...navigation.flatMap((group) =>
      group.items.flatMap((item) => {
        const items = [
          {
            label: item.label,
            href: item.href,
            groupLabel: group.label,
            parentLabel: undefined as string | undefined,
            external: item.external,
          },
        ];
        if (item.children) {
          items.push(
            ...item.children.map((child) => ({
              label: child.label,
              href: child.href,
              groupLabel: group.label,
              parentLabel: item.label,
              external: child.external,
            }))
          );
        }
        return items;
      })
    ),
    // Include help link
    {
      label: "Need help? Contact us",
      href: book.needHelp,
      groupLabel: undefined as string | undefined,
      parentLabel: undefined as string | undefined,
    },
  ];

  return (
    <aside
      className={cn(
        "flex flex-col h-full bg-sidebar border-r border-sidebar-border",
        "transition-all duration-300 ease-in-out",
        sidebarCollapsed ? "w-16" : "w-60",
        className
      )}
    >
      {/* Logo and Collapse/Expand Toggle */}
      <div
        className={cn(
          "relative flex flex-col items-center justify-center",
          "h-24 shrink-0",
          "bg-gradient-to-b from-sidebar-primary/10 to-sidebar-background",
          "border-b border-sidebar-border/60",
          "transition-all duration-300 ease-in-out",
          sidebarCollapsed ? "px-3" : "px-6"
        )}
      >
        {!sidebarCollapsed ? (
          <>
            <Link
              to="/"
              className="flex flex-col items-center gap-2 w-full group hover:bg-sidebar-accent/50 transition-colors duration-200 rounded-md py-3 px-3"
            >
              <IconLogo
                numberHost={hostNames[window.location.host.replace('account.', '')]}
                className="!m-0"
              />
              <span className="text-[11px] font-semibold uppercase tracking-widest text-sidebar-foreground/70">
                User Portal
              </span>
            </Link>

            {/* Collapse button - refined */}
            <button
              onClick={toggleSidebar}
              className={cn(
                "absolute right-2 top-1/2 -translate-y-1/2",
                "flex items-center justify-center rounded-md",
                "h-7 w-7 text-sm",
                "text-sidebar-foreground/40 hover:text-sidebar-foreground/70",
                "hover:bg-sidebar-accent/50",
                "transition-colors duration-150",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-sidebar-ring"
              )}
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </>
        ) : (
          <button
            onClick={toggleSidebar}
            className={cn(
              "flex flex-col items-center justify-center",
              "text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors duration-200",
              "w-full h-full rounded-md hover:bg-sidebar-accent/50"
            )}
            aria-label="Expand sidebar"
          >
            {/* Logo monogram - refined */}
            <div className="w-10 h-10 flex items-center justify-center rounded-md bg-sidebar-accent border border-sidebar-border">
              <span className="text-sidebar-primary font-bold text-lg">B</span>
            </div>
          </button>
        )}
      </div>

      {/* Search */}
      {sidebarCollapsed ? (
        <div className="px-2 pt-3 pb-2 border-b border-sidebar-border">
          <button
            onClick={handleSearchClick}
            className={cn(
              "flex items-center justify-center w-full h-9 rounded-md",
              "text-sidebar-foreground/60 hover:text-sidebar-foreground",
              "hover:bg-sidebar-accent/50 transition-colors duration-150",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
            )}
            aria-label="Search menu"
            title="Search (Ctrl+K)"
          >
            <Search className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="pt-3">
          <SidebarSearch ref={searchInputRef} items={searchableItems} collapsed={sidebarCollapsed} />
        </div>
      )}

      {/* Navigation */}
      <nav
        className="flex-1 overflow-y-auto py-4 px-2 pb-2 scrollbar-thin"
        aria-label="Main navigation"
      >
        {/* Top-level items (Home) - Primary Navigation */}
        <ul className="space-y-1 mb-4 pb-3 border-b border-sidebar-border/50">
          {topLevelItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <li key={item.href} className="group flex items-center">
                <Link
                  to={item.href}
                  className={cn(
                    "flex flex-1 items-center gap-3 rounded-md px-3 py-2",
                    "text-sm font-semibold",
                    "transition-colors duration-150",
                    "focus-visible:outline-none focus-visible:ring-2",
                    "focus-visible:ring-sidebar-ring",
                    isActive
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
                    sidebarCollapsed && "justify-center px-2"
                  )}
                  title={sidebarCollapsed ? item.label : undefined}
                  aria-current={isActive ? "page" : undefined}
                >
                  <item.icon
                    className={cn(
                      "h-5 w-5 shrink-0",
                      isActive
                        ? "text-sidebar-primary"
                        : "text-sidebar-foreground/70"
                    )}
                  />
                  {!sidebarCollapsed && <span className="flex-1">{item.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Navigation Sections */}
        {navigation.map((group) => {
          const sectionExpanded = isSectionExpanded(group);

          return (
            <div key={group.label} className={sidebarCollapsed ? "mb-1" : "mb-4"}>
              {!sidebarCollapsed && (
                <button
                  onClick={() => toggleSection(group.label)}
                  className={cn(
                    "w-full px-3 py-1.5 mb-1 flex items-center gap-2",
                    "text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider",
                    "hover:text-sidebar-foreground/70 transition-colors duration-150",
                    "rounded-md hover:bg-sidebar-accent/30"
                  )}
                  aria-expanded={sectionExpanded}
                  aria-label={`${group.label} section`}
                >
                  <span className="flex-1 text-left">{group.label}</span>
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5",
                      !sectionExpanded && "-rotate-90"
                    )}
                  />
                </button>
              )}
              {sectionExpanded && (
                <ul className="space-y-1">
                  {group.items.map((item, itemIndex) => {
                    const isActive =
                      location.pathname === item.href ||
                      (item.href !== "/" && location.pathname.startsWith(item.href));
                    const hasChildren = item.children && item.children.length > 0;
                    const expanded = hasChildren && isExpanded(item);

                    return (
                      <li key={`${item.href}-${itemIndex}`}>
                        {/* Parent item - expandable if has children */}
                        {hasChildren && !sidebarCollapsed ? (
                          <button
                            onClick={() => toggleExpanded(item.href)}
                            className={cn(
                              "flex w-full items-center gap-2.5 rounded-md pl-5 pr-3 py-2 group",
                              "text-sm font-medium",
                              "transition-colors duration-150",
                              "focus-visible:outline-none focus-visible:ring-2",
                              "focus-visible:ring-sidebar-ring",
                              isActive
                                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                : "text-sidebar-foreground/75 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                            )}
                            aria-expanded={expanded}
                            aria-label={item.label}
                          >
                            <item.icon
                              className={cn(
                                "h-[18px] w-[18px] shrink-0",
                                isActive
                                  ? "text-sidebar-primary"
                                  : "text-sidebar-foreground/70"
                              )}
                            />
                            <span className="flex-1 text-left">{item.label}</span>
                            <ChevronDown
                              className={cn(
                                "h-3.5 w-3.5 shrink-0 text-sidebar-foreground/40",
                                expanded && "rotate-180"
                              )}
                            />
                          </button>
                        ) : item.external ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              "flex items-center gap-2.5 rounded-md py-2",
                              "text-sm",
                              "transition-colors duration-150",
                              "focus-visible:outline-none focus-visible:ring-2",
                              "focus-visible:ring-sidebar-ring",
                              "text-sidebar-foreground/65 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground pl-5 pr-3",
                              sidebarCollapsed && "justify-center px-2"
                            )}
                            title={sidebarCollapsed ? item.label : undefined}
                          >
                            <item.icon
                              className={cn(
                                "h-[17px] w-[17px] shrink-0",
                                "text-sidebar-foreground/50"
                              )}
                            />
                            {!sidebarCollapsed && <span className="flex-1">{item.label}</span>}
                          </a>
                        ) : (
                          <Link
                            to={item.href}
                            className={cn(
                              "flex items-center gap-2.5 rounded-md py-2",
                              "text-sm",
                              "transition-colors duration-150",
                              "focus-visible:outline-none focus-visible:ring-2",
                              "focus-visible:ring-sidebar-ring",
                              isActive
                                ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium pl-5 pr-3"
                                : "text-sidebar-foreground/65 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground pl-5 pr-3",
                              sidebarCollapsed && "justify-center px-2"
                            )}
                            title={sidebarCollapsed ? item.label : undefined}
                            aria-current={isActive ? "page" : undefined}
                          >
                            <item.icon
                              className={cn(
                                "h-[17px] w-[17px] shrink-0",
                                isActive
                                  ? "text-sidebar-primary"
                                  : "text-sidebar-foreground/50"
                              )}
                            />
                            {!sidebarCollapsed && <span className="flex-1">{item.label}</span>}
                          </Link>
                        )}

                        {/* Children items */}
                        {hasChildren && expanded && !sidebarCollapsed && (
                          <ul className="ml-6 mt-1 space-y-0.5 border-l border-sidebar-border/60 pl-4">
                            {item.children!.map((child) => {
                              // A child that repeats its parent's link (an "Overview") is active only on
                              // that exact page, not on every page below it.
                              const isChildActiveItem =
                                !child.external && (
                                  location.pathname === child.href ||
                                  (child.href !== "/" && child.href !== item.href && location.pathname.startsWith(child.href))
                                );

                              const childClassName = cn(
                                "flex flex-1 items-center gap-2 rounded-md px-2 py-1.5",
                                "text-[13px]",
                                "transition-colors duration-150",
                                "focus-visible:outline-none focus-visible:ring-2",
                                "focus-visible:ring-sidebar-ring",
                                isChildActiveItem
                                  ? "text-sidebar-primary font-medium bg-sidebar-accent/40"
                                  : "text-sidebar-foreground/60 hover:text-sidebar-foreground/90 hover:bg-sidebar-accent/30"
                              );

                              return (
                                <li key={child.href} className="group flex items-center">
                                  {child.external ? (
                                    <a
                                      href={child.href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={childClassName}
                                      aria-label={`${child.label} (opens in new tab)`}
                                    >
                                      <child.icon className="h-3.5 w-3.5 shrink-0" />
                                      <span className="flex-1">{child.label}</span>
                                    </a>
                                  ) : (
                                    <Link
                                      to={child.href}
                                      className={childClassName}
                                      aria-current={isChildActiveItem ? "page" : undefined}
                                    >
                                      <child.icon className="h-3.5 w-3.5 shrink-0" />
                                      <span className="flex-1">{child.label}</span>
                                    </Link>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      {/* Help Footer */}
      <div className="shrink-0 border-t border-sidebar-border">
        <Link
          to={book.needHelp}
          className={cn(
            "flex items-center gap-3 rounded-md px-3 py-3 mx-2 my-2",
            "text-sm font-medium",
            "text-sidebar-foreground/70 hover:text-sidebar-foreground",
            "hover:bg-sidebar-accent/50",
            "transition-colors duration-150",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-sidebar-ring",
            location.pathname === book.needHelp && "bg-sidebar-accent text-sidebar-accent-foreground",
            sidebarCollapsed && "justify-center px-2"
          )}
          title={sidebarCollapsed ? "Need help? Contact us" : undefined}
          aria-label="Need help? Contact us"
        >
          <HelpCircle className="h-5 w-5 shrink-0" />
          {!sidebarCollapsed && <span>Need help? Contact us</span>}
        </Link>
      </div>
    </aside>
  );
}
